/**
 * CSV 출력 미리보기 모달 (M22 #4).
 *
 * 다운로드 없이 4종 CSV(납품/확인 × 정상/반려)를 표로 미리 확인한다.
 * 상단 토글로 종류를 전환하고, 선택된 종류를 JSON(headers/rows)로 조회해
 * 렌더한다. 정렬·시퀀스·컬럼 매핑은 다운로드와 동일 소스(백엔드).
 *
 * 두 가지 모드:
 *   - 단일 표(기본): Job 미리보기 등. `fetchPreview`/`jobId`.
 *   - 폴더별 그룹(의뢰): `fetchGroupedPreview` 주입 시 작업(폴더) 단위로
 *     아코디언 형태로 펼쳐 확인한다 (M25).
 */

import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, ChevronDown, ChevronRight, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import { getCsvPreview, type CsvPreview, type CsvType } from "@/lib/api/jobs";
import type { RequestCsvPreview } from "@/lib/api/requests";
import { cn } from "@/lib/cn";

interface Props {
  successCount: number;
  rejectedCount: number;
  onClose: () => void;
  /** Job 미리보기(기본). request 등 다른 소스는 fetchPreview 로 주입. */
  jobId?: string;
  /** 단일 표 미리보기 fetcher 를 직접 주입. 미지정 시 jobId 기반. */
  fetchPreview?: (type: CsvType) => Promise<CsvPreview>;
  /** 폴더(작업)별 그룹 미리보기 fetcher — 지정 시 아코디언 렌더(의뢰). */
  fetchGroupedPreview?: (type: CsvType) => Promise<RequestCsvPreview>;
  /** react-query 캐시 키 prefix (소스 구분용). */
  cacheKey?: string;
  /**
   * 단일 표(Job) 미리보기에서 행 클릭 시 해당 케이스 편집을 요청하는 콜백.
   * 지정 시 행이 클릭 가능해지고, rows[i] → file_ids[i] 로 매핑해 호출한다.
   * (그룹/의뢰 미리보기에는 적용하지 않음)
   */
  onEditRow?: (fileId: string) => void;
}

interface TypeOption {
  type: CsvType;
  i18nKey: "delivery_ok" | "delivery_rejected" | "review_ok" | "review_rejected";
  showWhen: "ok" | "rejected";
}

const TYPE_OPTIONS: TypeOption[] = [
  { type: "delivery", i18nKey: "delivery_ok", showWhen: "ok" },
  { type: "review", i18nKey: "review_ok", showWhen: "ok" },
  { type: "rejected_delivery", i18nKey: "delivery_rejected", showWhen: "rejected" },
  { type: "rejected_review", i18nKey: "review_rejected", showWhen: "rejected" },
];

function isGrouped(data: CsvPreview | RequestCsvPreview): data is RequestCsvPreview {
  return "groups" in data;
}

function CsvTable({
  headers,
  rows,
  stickyHeader = true,
  onRowClick,
}: {
  headers: string[];
  rows: string[][];
  stickyHeader?: boolean;
  /** 지정 시 행이 클릭 가능해지고 클릭 시 rowIndex 를 넘긴다. */
  onRowClick?: (rowIndex: number) => void;
}) {
  const clickable = Boolean(onRowClick);
  return (
    <table className="min-w-full border-collapse text-xs">
      <thead className={cn("bg-slate-100", stickyHeader && "sticky top-0 z-10")}>
        <tr>
          {headers.map((h, i) => (
            <th
              key={`h-${i}`}
              className="whitespace-nowrap border border-slate-200 px-2 py-1.5 text-left font-semibold text-slate-700"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, r) => (
          <tr
            key={`r-${r}`}
            className={cn(
              "odd:bg-white even:bg-slate-50/60",
              clickable && "cursor-pointer hover:bg-blue-50",
            )}
            onClick={clickable ? () => onRowClick?.(r) : undefined}
          >
            {row.map((cell, c) => (
              <td
                key={`c-${r}-${c}`}
                className="max-w-[280px] truncate border border-slate-200 px-2 py-1 text-slate-700"
                title={cell}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function CsvPreviewModal({
  jobId,
  successCount,
  rejectedCount,
  onClose,
  fetchPreview,
  fetchGroupedPreview,
  cacheKey,
  onEditRow,
}: Props) {
  const { t } = useTranslation();
  const grouped = Boolean(fetchGroupedPreview);
  const doFetch: (type: CsvType) => Promise<CsvPreview | RequestCsvPreview> =
    fetchGroupedPreview ?? fetchPreview ?? ((type) => getCsvPreview(jobId as string, type));
  const keyPrefix = cacheKey ?? `job:${jobId}`;

  // 데이터가 있는 종류를 기본 선택 (확인용 정상 → 반려 확인용 → 납품용 순).
  const initialType: CsvType =
    successCount > 0 ? "review" : rejectedCount > 0 ? "rejected_review" : "delivery";
  const [type, setType] = useState<CsvType>(initialType);
  // 접힌 폴더 job_id 집합 (기본: 모두 펼침).
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // 본문 가로 스크롤 컨테이너 (좌우 이동 버튼용).
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollX = (dir: -1 | 1) =>
    scrollRef.current?.scrollBy({ left: dir * 480, behavior: "smooth" });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // 종류 전환 시 펼침 상태 초기화.
  useEffect(() => {
    setCollapsed(new Set());
  }, [type]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["csv-preview", keyPrefix, type],
    queryFn: () => doFetch(type),
  });

  const isEnabled = (o: TypeOption) => (o.showWhen === "ok" ? successCount > 0 : rejectedCount > 0);
  const errorMessage = error instanceof ApiError ? error.detail : (error as Error | null)?.message;

  const groupedData = data && isGrouped(data) ? data : null;
  const rowCount = data ? (isGrouped(data) ? data.total : data.count) : 0;
  const isEmpty = data
    ? isGrouped(data)
      ? data.total === 0 || data.groups.length === 0
      : data.rows.length === 0
    : true;

  const toggleFolder = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () =>
    setCollapsed(new Set(groupedData ? groupedData.groups.map((g) => g.job_id) : []));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[90vh] w-[95vw] max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-800">
            {t("jobs.detail.csv.preview.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100"
            aria-label={t("common.actions.close")}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* 종류 토글 */}
        <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-5 py-2.5">
          {TYPE_OPTIONS.map((o) => {
            const enabled = isEnabled(o);
            const active = o.type === type;
            return (
              <button
                key={o.type}
                type="button"
                disabled={!enabled}
                onClick={() => setType(o.type)}
                className={cn(
                  "rounded-md border px-2.5 py-1 text-xs font-medium transition",
                  active
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : enabled
                      ? "border-slate-200 text-slate-600 hover:bg-slate-50"
                      : "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300",
                )}
              >
                {t(`jobs.detail.csv.variants.${o.i18nKey}.label`)}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-2">
            {data && !isEmpty && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => scrollX(-1)}
                  className="rounded border border-slate-200 p-1 text-slate-500 hover:bg-slate-50"
                  aria-label={t("jobs.detail.csv.preview.scrollLeft")}
                  title={t("jobs.detail.csv.preview.scrollLeft")}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollX(1)}
                  className="rounded border border-slate-200 p-1 text-slate-500 hover:bg-slate-50"
                  aria-label={t("jobs.detail.csv.preview.scrollRight")}
                  title={t("jobs.detail.csv.preview.scrollRight")}
                >
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            {grouped && groupedData && !isEmpty && (
              <>
                <button
                  type="button"
                  onClick={expandAll}
                  className="rounded border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50"
                >
                  {t("requests.detail.csv.preview.expandAll")}
                </button>
                <button
                  type="button"
                  onClick={collapseAll}
                  className="rounded border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50"
                >
                  {t("requests.detail.csv.preview.collapseAll")}
                </button>
              </>
            )}
            {data && (
              <span className="text-[11px] text-slate-400">
                {t("jobs.detail.csv.preview.rowCount", { count: rowCount })}
              </span>
            )}
          </div>
        </div>

        {/* 본문 — 가로 스크롤바 상시 노출(overflow-x-scroll) + 좌우 버튼 이동 */}
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto overflow-x-scroll [scrollbar-width:auto]"
        >
          {isLoading ? (
            <div className="flex h-full items-center justify-center gap-2 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("jobs.detail.csv.preview.loading")}
            </div>
          ) : isError ? (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-red-600">
              {t("jobs.detail.csv.preview.error")}
              {errorMessage ? `: ${errorMessage}` : ""}
            </div>
          ) : !data || isEmpty ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              {t("jobs.detail.csv.preview.empty")}
            </div>
          ) : groupedData ? (
            <div className="divide-y divide-slate-100">
              {groupedData.groups.map((g) => {
                const open = !collapsed.has(g.job_id);
                return (
                  <div key={g.job_id}>
                    <button
                      type="button"
                      onClick={() => toggleFolder(g.job_id)}
                      className="flex w-full items-center justify-between gap-2 bg-slate-50 px-4 py-2 text-left hover:bg-slate-100"
                    >
                      <span className="flex min-w-0 items-center gap-1.5">
                        {open ? (
                          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                        )}
                        <span
                          className="truncate text-xs font-medium text-slate-700"
                          title={g.folder}
                        >
                          {g.folder}
                        </span>
                      </span>
                      <span className="shrink-0 text-[11px] text-slate-400">
                        {t("jobs.detail.csv.preview.rowCount", { count: g.count })}
                      </span>
                    </button>
                    {open && (
                      <CsvTable headers={groupedData.headers} rows={g.rows} stickyHeader={false} />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <CsvTable
              headers={(data as CsvPreview).headers}
              rows={(data as CsvPreview).rows}
              onRowClick={
                onEditRow
                  ? (r) => {
                      const fileId = (data as CsvPreview).file_ids?.[r];
                      if (fileId) onEditRow(fileId);
                    }
                  : undefined
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
